import React, { useState, useEffect } from 'react';
import api from '../services/api';
import Select from 'react-select';
import { useAcademicYear } from '../contexts/AcademicYearContext';
import { useAuth } from '../contexts/AuthContext';
import { FiEdit, FiTrash2, FiShield, FiUserX, FiClock } from 'react-icons/fi';
import ConditionalButton from '../components/common/ConditionalButton';
import ConditionalActions from '../components/common/ConditionalActions';
import DispensesModal from '../components/DispensesModal';
import DesinscriptionsTemporairesModal from '../components/DesinscriptionsTemporairesModal';
import AbsenceModal from '../components/AbsenceModal';
import Swal from 'sweetalert2';

function formatDateFR(dateStr) {
  if (!dateStr) return '';
  const [y, m, d] = dateStr.split('-');
  return `${d}/${m}/${y}`;
}

const Inscriptions = () => {
  const { selectedYear } = useAcademicYear();
  // eslint-disable-next-line no-unused-vars
  const { user: _user } = useAuth(); // Préfixe _ pour variable intentionnellement inutilisée
  const [inscriptions, setInscriptions] = useState([]);
  const [eleves, setEleves] = useState([]);
  const [ouvertures, setOuvertures] = useState([]);
  const [search, setSearch] = useState('');
  const [ouvertureClasseFilter, setOuvertureClasseFilter] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showDispensesModal, setShowDispensesModal] = useState(false);
  const [showDesinscriptionsModal, setShowDesinscriptionsModal] = useState(false);
  const [showAbsenceModal, setShowAbsenceModal] = useState(false);
  const [selectedInscription, setSelectedInscription] = useState(null);
  const [form, setForm] = useState({
    eleve_id: '',
    ouverture_classe_id: '',
    date_inscription: '',
    redoublant: false,
    boursier: false,
    validation: true,
    type_sejour: ''
  });

 useEffect(() => {
  fetchAll();
  api.get('/eleves').then(res => setEleves(res.data.data || []));
  api.get('/ouvertures-classes').then(res => {
    console.log('Ouvertures chargées:', res.data.data);
    setOuvertures(res.data.data || []);
  });
}, []);

  const fetchAll = () => {
    api.get('/inscriptions').then(res => {
      console.log('Toutes les inscriptions chargées:', res.data.data);
      setInscriptions(res.data.data || []);
    });
  };

  const openAdd = () => {
    setEditing(null);
    setForm({
      eleve_id: '',
      ouverture_classe_id: '',
      date_inscription: new Date().toISOString().slice(0, 10),
      redoublant: false,
      boursier: false,
      validation: true,
      type_sejour: ''
    });
    setShowModal(true);
  };

  const openEdit = (insc) => {
    setEditing(insc);
    setForm({ ...insc });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditing(null);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(f => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSelect = (name, option) => {
    setForm(f => ({ ...f, [name]: option ? option.value : '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (editing) {
      await api.put(`/inscriptions/${editing.id}`, form);
    } else {
      await api.post('/inscriptions', form);
    }
    fetchAll();
    closeModal();
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: 'Supprimer cette inscription ?',
      text: 'Cette action est irréversible.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Oui, supprimer',
      cancelButtonText: 'Annuler',
    });
    if (result.isConfirmed) {
      await api.delete(`/inscriptions/${id}`);
      fetchAll();
      Swal.fire('Supprimé !', "L'inscription a été supprimée.", 'success');
    }
  };

  // --- Pagination locale ---
  const [page, setPage] = useState(1);
  const pageSize = 20;

  // Recherche et filtrage avancé
  const filteredInscriptions = inscriptions.filter(insc => {
    // Filtrage par année scolaire (ouverture de classe)
    const ouverture = ouvertures.find(o => o.ouverture_classe_id === insc.ouverture_classe_id);
    if (!ouverture || (selectedYear && ouverture.annee_scolaire_id !== selectedYear.id)) return false;
    // Filtrage par classe
    if (ouvertureClasseFilter) {
      console.log('Comparaison', insc.ouverture_classe_id, typeof insc.ouverture_classe_id, ouvertureClasseFilter.value, typeof ouvertureClasseFilter.value);
      if (String(insc.ouverture_classe_id) !== String(ouvertureClasseFilter.value)) {
        return false;
      }
    }
    // Recherche sur élève
    const eleve = eleves.find(e => e.id === insc.eleve_id);
    if (search) {
      const s = search.toLowerCase();
      if (
        !(eleve && (
          eleve.nom.toLowerCase().includes(s) ||
          eleve.prenom.toLowerCase().includes(s) ||
          (eleve.nom + ' ' + eleve.prenom).toLowerCase().includes(s) ||
          (eleve.matricule && eleve.matricule.toLowerCase().includes(s))
        ))
      ) {
        return false;
      }
    }
    return true;
  });
  const totalPages = Math.ceil(filteredInscriptions.length / pageSize) || 1;
  const paginatedInscriptions = filteredInscriptions.slice((page - 1) * pageSize, page * pageSize);
  console.log('Ouvertures (pour options):', ouvertures);
  // Liste des ouvertures de classes pour le filtre (une option = une ouverture de classe unique année+classe)
  // Select n'affiche que les ouvertures de classes de l'année sélectionnée
  // Correction : value = o.id (ouverture_classe_id), label = nom de la classe
  const ouverturesOptions = ouvertures
    .filter(o => selectedYear && o.annee_scolaire_id === selectedYear.id)
    .map(o => ({
      value: o.ouverture_classe_id, // id unique de l'ouverture de classe
      label:
        (typeof o.classe === 'string'
          ? o.classe
          : (o.classe?.classe || o.classe?.abrev || String(o.classe_id)))
        + (o.classe?.abrev ? ` (${o.classe.abrev})` : '')
    }));

    
    // Effectif par sexe si une classe est filtrée
    let effectifBox = null;
    if (ouvertureClasseFilter) {
    const classeNom = ouvertureClasseFilter.label;
    const total = filteredInscriptions.length;
    let nbG = 0, nbF = 0;
    filteredInscriptions.forEach(insc => {
      const eleve = eleves.find(e => e.id === insc.eleve_id);
      if (eleve && eleve.sexe) {
        if (eleve.sexe.toUpperCase() === 'M') nbG++;
        if (eleve.sexe.toUpperCase() === 'F') nbF++;
      }
    });
    effectifBox = (
      <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded text-blue-900 font-semibold flex items-center gap-4">
        Effectif de la {classeNom} : {total} &nbsp; - &nbsp; G({nbG}) F({nbF})
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6 gap-4">
        <h1 className="text-2xl font-semibold text-gray-900">Inscriptions</h1>
        <div className="flex flex-col md:flex-row gap-2 md:items-center">
          <input
            type="text"
            placeholder="Rechercher élève..."
            className="border rounded px-3 py-2"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <Select
            options={ouverturesOptions}
            value={ouvertureClasseFilter}
            onChange={setOuvertureClasseFilter}
            isClearable
            placeholder="Filtrer par ouverture de classe"
            classNamePrefix="react-select"
            styles={{ container: base => ({ ...base, minWidth: 200 }) }}
          />
          <ConditionalButton
            entity="inscriptions"
            permission="create"
            onClick={openAdd}
            className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700"
          >
            Nouvelle inscription
          </ConditionalButton>
        </div>
      </div>
      {effectifBox}
      <div className="bg-white shadow rounded-lg overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-2">N° Inscr.</th>
              <th className="px-4 py-2">Élève</th>
              <th className="px-4 py-2">Classe</th>
              <th className="px-4 py-2">Date</th>
              <th className="px-4 py-2">Validation</th>
              <th className="px-4 py-2">Type séjour</th>
              <th className="px-4 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredInscriptions.length === 0 ? (
              <tr><td colSpan="8" className="text-center py-4 text-gray-500">Aucune inscription</td></tr>
            ) : (
              paginatedInscriptions.map(insc => {
                const eleve = eleves.find(e => e.id === insc.eleve_id);
                const ouverture = ouvertures.find(o => o.ouverture_classe_id === insc.ouverture_classe_id);
                return (
                  <tr key={insc.id}>
                    <td className="px-4 py-2">{insc.id}</td>
                    <td className="px-4 py-2">{eleve ? eleve.nom + ' ' + eleve.prenom : insc.eleve_id}</td>
                    <td className="px-4 py-2">{
                      ouverture
                        ? (
                            ouverture.nom && typeof ouverture.nom === 'string'
                              ? ouverture.nom
                              : (
                                  ouverture.classe && typeof ouverture.classe === 'object'
                                    ? (ouverture.classe.classe || ouverture.classe.abrev || '')
                                    : (typeof ouverture.classe === 'string' ? ouverture.classe : '')
                                )
                          )
                        : insc.ouverture_classe_id
                    }</td>
                    <td className="px-4 py-2">{formatDateFR(insc.date_inscription)}</td>
                    {/* <td className="px-4 py-2 text-center">{insc.redoublant ? 'Oui' : 'Non'}</td>
                    <td className="px-4 py-2 text-center">{insc.boursier ? 'Oui' : 'Non'}</td> */}
                    <td className="px-4 py-2 text-center">{insc.validation ? 'Oui' : 'Non'}</td>
                    <td className="px-4 py-2">{insc.type_sejour}</td>
                    <td className="px-4 py-2 text-right">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => {
                            setSelectedInscription({ ...insc, nom: eleve?.nom, prenom: eleve?.prenom });
                            setShowDispensesModal(true);
                          }}
                          className="text-blue-600 hover:text-blue-900 p-1 rounded transition-colors"
                          title="Gérer les dispenses"
                        >
                          <FiShield size={16} />
                        </button>
                        <button
                          onClick={() => {
                            setSelectedInscription({ ...insc, nom: eleve?.nom, prenom: eleve?.prenom });
                            setShowDesinscriptionsModal(true);
                          }}
                          className="text-orange-600 hover:text-orange-900 p-1 rounded transition-colors"
                          title="Gérer les désinscriptions temporaires"
                        >
                          <FiUserX size={16} />
                        </button>
                        <button
                          onClick={() => {
                            setSelectedInscription({ ...insc, nom: eleve?.nom, prenom: eleve?.prenom });
                            setShowAbsenceModal(true);
                          }}
                          className="text-purple-600 hover:text-purple-900 p-1 rounded transition-colors"
                          title="Ajouter une absence"
                        >
                          <FiClock size={16} />
                        </button>
                        <ConditionalActions
                          entity="inscriptions"
                          onEdit={() => openEdit(insc)}
                          onDelete={() => handleDelete(insc.id)}
                          editIcon={FiEdit}
                          deleteIcon={FiTrash2}
                          className="flex space-x-2"
                        />
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
      {/* Pagination locale harmonisée */}
      {!showModal && totalPages > 1 && (
        <div className="flex justify-center items-center gap-1 mt-4 flex-wrap pb-4">
          <button onClick={() => setPage(1)} disabled={page === 1} className="px-2 py-1 rounded bg-gray-200 hover:bg-gray-300 disabled:opacity-50 text-sm">Premier</button>
          <button onClick={() => setPage(page - 1)} disabled={page === 1} className="px-2 py-1 rounded bg-gray-200 hover:bg-gray-300 disabled:opacity-50 text-sm">Précédent</button>
          {(() => {
            const pages = [];
            if (totalPages <= 10) {
              for (let i = 1; i <= totalPages; i++) {
                pages.push(i);
              }
            } else {
              for (let i = 1; i <= 5; i++) pages.push(i);
              if (page > 7 && page < totalPages - 6) {
                pages.push('...');
                for (let i = page - 1; i <= page + 1; i++) pages.push(i);
                pages.push('...');
              } else {
                pages.push('...');
              }
              for (let i = totalPages - 4; i <= totalPages; i++) {
                if (i > 5) pages.push(i);
              }
            }
            return [...new Set(pages)].map((p, idx) =>
              p === '...'
                ? <span key={"ellipsis-" + idx} className="mx-0.5 px-2">...</span>
                : <button
                    key={p}
                    className={`px-2 py-1 rounded mx-0.5 text-sm ${p === page ? 'bg-primary-600 text-white' : 'bg-gray-100 hover:bg-primary-100 text-gray-700'}`}
                    onClick={() => setPage(p)}
                    disabled={p === page}
                  >{p}</button>
            );
          })()}
          <button onClick={() => setPage(page + 1)} disabled={page === totalPages} className="px-2 py-1 rounded bg-gray-200 hover:bg-gray-300 disabled:opacity-50 text-sm">Suivant</button>
          <button onClick={() => setPage(totalPages)} disabled={page === totalPages} className="px-2 py-1 rounded bg-gray-200 hover:bg-gray-300 disabled:opacity-50 text-sm">Dernier</button>
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg p-8 w-full max-w-lg">
            <h2 className="text-xl font-semibold mb-4">{editing ? 'Modifier' : 'Nouvelle'} inscription</h2>
            <form onSubmit={handleSubmit}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Élève *</label>
                <Select
                  options={eleves.map(e => ({ value: e.id, label: e.nom + ' ' + e.prenom }))}
                  value={eleves.find(e => e.id === form.eleve_id) ? { value: form.eleve_id, label: eleves.find(e => e.id === form.eleve_id).nom + ' ' + eleves.find(e => e.id === form.eleve_id).prenom } : null}
                  onChange={opt => handleSelect('eleve_id', opt)}
                  isClearable
                  placeholder="Sélectionner un élève"
                  classNamePrefix="react-select"
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Ouverture de classe *</label>
                <Select
                  options={ouvertures
                    .filter(o => selectedYear && o.annee_scolaire_id === selectedYear.id)
                    .map(o => ({
                      value: o.ouverture_classe_id,
                      label: o.nom
                        || (typeof o.classe === 'string'
                          ? o.classe
                          : (o.classe?.classe || o.classe?.abrev || String(o.ouverture_classe_id)))
                    }))}
                  value={(() => {
                    const o = ouvertures.find(o => o.ouverture_classe_id === form.ouverture_classe_id);
                    return o && selectedYear && o.annee_scolaire_id === selectedYear.id
                      ? {
                          value: form.ouverture_classe_id,
                          label: o.nom
                            || (typeof o.classe === 'string'
                              ? o.classe
                              : (o.classe?.classe || o.classe?.abrev || String(o.ouverture_classe_id)))
                        }
                      : null;
                  })()}
                  onChange={opt => handleSelect('ouverture_classe_id', opt)}
                  isClearable
                  placeholder="Sélectionner une classe"
                  classNamePrefix="react-select"
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Date d'inscription</label>
                <input type="date" name="date_inscription" value={form.date_inscription || ''} onChange={handleChange} className="block w-full rounded-lg border border-gray-300 px-3 py-2" />
              </div>
              <div className="flex gap-4 mb-4">
                <label className="flex items-center gap-2">
                  <input type="checkbox" name="redoublant" checked={!!form.redoublant} onChange={handleChange} /> Redoublant
                </label>
                <label className="flex items-center gap-2">
                  <input type="checkbox" name="boursier" checked={!!form.boursier} onChange={handleChange} /> Boursier
                </label>
                <label className="flex items-center gap-2">
                  <input type="checkbox" name="validation" checked={!!form.validation} onChange={handleChange} /> Validé
                </label>
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Type de séjour</label>
                <Select
                  options={[
                    { value: 'externe', label: 'Externe' },
                    { value: 'interne', label: 'Interne' },
                    { value: 'demi-pensionnaire', label: 'Demi-pensionnaire' }
                  ]}
                  value={form.type_sejour ? { value: form.type_sejour, label: form.type_sejour.charAt(0).toUpperCase() + form.type_sejour.slice(1).replace('-', ' ') } : null}
                  onChange={opt => setForm(f => ({ ...f, type_sejour: opt ? opt.value : '' }))}
                  isClearable
                  placeholder="Sélectionner le type de séjour"
                  classNamePrefix="react-select"
                />
              </div>
              <div className="flex justify-end gap-2">
                <button type="button" className="px-4 py-2 bg-gray-200 rounded-lg" onClick={closeModal}>Annuler</button>
                <button type="submit" className="px-4 py-2 bg-primary-600 text-white rounded-lg">Enregistrer</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de gestion des dispenses */}
      <DispensesModal
        isOpen={showDispensesModal}
        onClose={() => {
          setShowDispensesModal(false);
          setSelectedInscription(null);
        }}
        inscriptionId={selectedInscription?.id}
        inscriptionData={selectedInscription}
      />

      {/* Modal de gestion des désinscriptions temporaires */}
      <DesinscriptionsTemporairesModal
        isOpen={showDesinscriptionsModal}
        onClose={() => {
          setShowDesinscriptionsModal(false);
          setSelectedInscription(null);
        }}
        inscriptionId={selectedInscription?.id}
        inscriptionData={selectedInscription}
      />

      {/* Modal d'ajout d'absence */}
      <AbsenceModal
        isOpen={showAbsenceModal}
        onClose={() => {
          setShowAbsenceModal(false);
          setSelectedInscription(null);
        }}
        inscriptionId={selectedInscription?.id}
        inscriptionData={selectedInscription}
      />
    </div>
  );
};

export default Inscriptions;
